import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";

// --- Google report types (GET /api/google/report/<clientId>) ---

export interface GbpTotals {
  impressionsMaps: number;
  impressionsSearch: number;
  impressions: number;
  callClicks: number;
  websiteClicks: number;
  directionRequests: number;
  conversations: number;
  bookings: number;
  foodOrders: number;
  foodMenuClicks: number;
  /** callClicks + websiteClicks + directionRequests + conversations +
   *  bookings + foodOrders. */
  actions: number;
}

export interface Ga4Totals {
  sessions: number;
  engagedSessions: number;
  /** Null when the month has no ga4_monthly_totals row: users do not add
   *  across daily rows. */
  totalUsers: number | null;
  newUsers: number | null;
  keyEvents: number;
  /** engagedSessions / sessions, a ratio. */
  engagementRate: number | null;
  /** Sessions and key events from the paid channel groups. */
  paidSessions: number;
  paidKeyEvents: number;
}

export interface AdsTotals {
  impressions: number;
  clicks: number;
  costMinor: number;
  conversions: number;
  conversionsValue: number;
  phoneCalls: number;
  /** clicks / impressions, a ratio. */
  ctr: number | null;
  cpcMinor: number | null;
}

/** A month in progress against the same days of the month before: day 1
 *  to `through` (the source's settled day) and day 1 to `previousThrough`,
 *  summed from the daily rows; null = no rows in that window. GA4 users
 *  stay null: they do not add across days. */
export interface GoogleToDate<T> {
  through: string;
  previousThrough: string;
  current: T | null;
  previous: T | null;
}

/** Running total of the source's headline metric (Business Profile
 *  actions, GA4 sessions, Ads clicks) by day of month: `current` through
 *  the settled day only, `previous` for the whole month before. */
export interface GooglePace {
  current: Array<{ day: number; value: number }>;
  previous: Array<{ day: number; value: number }>;
}

/** Shared by the three sources. `current`/`previous` null = no rows in
 *  that month (a gap, never zeros). `complete` = the source cursor has
 *  passed the month's last day plus its settle days. `linked` false = the
 *  client no longer names this account, so the rows are its history and no
 *  new sync will come. */
interface GoogleSourceMonth<T> {
  linked: boolean;
  /** The night the sync last ran, not the newest data day. */
  fetchedThrough: string | null;
  complete: boolean;
  /** ISO date this source's month becomes final. */
  finalFrom: string;
  current: T | null;
  previous: T | null;
  /** Null once complete, or when the settled day is before the 1st. */
  toDate: GoogleToDate<T> | null;
  pace: GooglePace;
}

export interface GoogleBusinessProfileReport extends GoogleSourceMonth<GbpTotals> {
  locationId: string;
  daily: Array<{ day: string; impressions: number; actions: number }>;
  keywords: Array<{ keyword: string; impressions: number | null; belowThreshold: boolean }>;
}

export interface GoogleWebsiteReport extends GoogleSourceMonth<Ga4Totals> {
  propertyId: string;
  channels: Array<{ channelGroup: string; sessions: number; keyEvents: number }>;
  keyEvents: Array<{ eventName: string; keyEvents: number }>;
}

export interface GoogleAdsCampaignRow {
  campaignId: string;
  name: string;
  /** Null when the campaign has no reported-campaign row. */
  status: string | null;
  /** Google's channel: "SEARCH", "PERFORMANCE_MAX"; null as status. */
  channelType: string | null;
  /** "YYYY-MM-DD"; null as status. */
  startDate: string | null;
  /** Null while it runs, or as status. */
  endDate: string | null;
  impressions: number;
  clicks: number;
  costMinor: number;
  conversions: number;
  phoneCalls: number;
  /** Clicks per day of the month, a day without a row left out (a gap,
   *  never a zero). */
  daily: Array<{ date: string; clicks: number }>;
}

export interface GoogleAdsReport extends GoogleSourceMonth<AdsTotals> {
  customerId: string;
  campaigns: GoogleAdsCampaignRow[];
}

/** A null source is not linked to the client. */
export interface GoogleReport {
  year: number;
  month: number;
  businessProfile: GoogleBusinessProfileReport | null;
  website: GoogleWebsiteReport | null;
  googleAds: GoogleAdsReport | null;
}

export const useGoogleReport = (
  clientId: string,
  year: number,
  month: number
) => {
  // Cached per client and month, so the three Google panels, in the
  // Reporting rail and in tiles, share one request.
  const query = useQuery({
    queryKey: ["google-report", clientId, year, month],
    queryFn: () => apiClient.get<GoogleReport>(`/api/google/report/${clientId}`, { params: { year, month } }),
    enabled: Boolean(clientId),
  });
  const apiErr = query.error as { response?: { data?: { error?: string } }; message?: string } | null;
  const error = apiErr ? apiErr.response?.data?.error || apiErr.message || "Failed to load Google report" : null;

  return { data: query.data ?? null, isLoading: query.isLoading, error };
};
